import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { format, startOfWeek, addDays, subDays, startOfMonth, endOfMonth, subMonths, addMonths } from "date-fns";
import { toZonedTime } from "date-fns-tz";

export function DoseHeatmap() {
  const [history, setHistory] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    return toZonedTime(new Date(), "America/Denver");
  });

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await fetch("/api/dose/history");
      const data = await response.json();
      setHistory(data.history || {});
    } catch (error) {
      console.error("Failed to fetch dose history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateHeatmapData = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const today = toZonedTime(new Date(), "America/Denver");

    // Start from the beginning of the week that contains the first day of the month
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
    // End at the end of the week that contains the last day of the month
    const endDate = startOfWeek(addDays(monthEnd, 6), { weekStartsOn: 0 });

    const weeks: Date[][] = [];
    let currentDate = new Date(startDate);

    // Generate weeks for the month (usually 4-6 weeks)
    while (currentDate <= endDate) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(currentDate));
        currentDate = addDays(currentDate, 1);
      }
      weeks.push(week);
    }

    return weeks;
  };

  const getCellColor = (date: Date | null) => {
    if (!date) return "bg-gray-100";

    const dateKey = format(date, "yyyy-MM-dd");
    const hasDose = history[dateKey] > 0;

    const today = toZonedTime(new Date(), "America/Denver");
    const isToday = format(date, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");
    const isFuture = date > today;
    const isCurrentMonth = date.getMonth() === currentMonth.getMonth() && date.getFullYear() === currentMonth.getFullYear();

    if (isFuture) return "bg-gray-100";
    if (!isCurrentMonth) return "bg-gray-100 opacity-30"; // Fade out days from adjacent months
    if (hasDose) return isToday ? "bg-green-500 ring-2 ring-green-600" : "bg-green-500";
    if (isToday) return "bg-yellow-200 ring-2 ring-yellow-400";
    return "bg-gray-200";
  };

  const getCellTitle = (date: Date | null) => {
    if (!date) return "";

    const dateKey = format(date, "yyyy-MM-dd");
    const doseCount = history[dateKey] || 0;
    const dateStr = format(date, "MMM d, yyyy");

    if (doseCount > 0) {
      return `${dateStr}: Medication taken`;
    }
    return `${dateStr}: No dose recorded`;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const weeks = generateHeatmapData();
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const today = toZonedTime(new Date(), "America/Denver");

  // Check navigation boundaries
  const isCurrentMonth = currentMonth.getMonth() === today.getMonth() && currentMonth.getFullYear() === today.getFullYear();
  const canGoNext = !isCurrentMonth;

  // Allow going back up to 12 months from current date
  const twelveMonthsAgo = subMonths(today, 12);
  const canGoPrevious = currentMonth > twelveMonthsAgo;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Dose History</CardTitle>
          <CardDescription>Loading your medication history...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Dose History</CardTitle>
            <CardDescription>Your medication tracking for {format(currentMonth, "MMMM yyyy")}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousMonth}
              disabled={!canGoPrevious}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextMonth}
              disabled={!canGoNext}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex justify-center">
          <div>
            {/* Month header */}
            <div className="flex justify-center mb-4">
              <div className="text-lg font-semibold">
                {format(currentMonth, "MMMM yyyy")}
              </div>
            </div>

            {/* Week day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map((day) => (
                <div key={day} className="text-xs text-muted-foreground text-center w-8 h-6 flex items-center justify-center">
                  {day.slice(0, 3)}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid gap-1">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="grid grid-cols-7 gap-1">
                  {week.map((date, dayIndex) => (
                    <div
                      key={dayIndex}
                      className={`w-8 h-8 rounded-sm ${getCellColor(date)} transition-all hover:ring-2 hover:ring-offset-1 flex items-center justify-center text-xs cursor-pointer`}
                      title={getCellTitle(date)}
                    >
                      {date ? date.getDate() : ""}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-4 mt-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-gray-200" />
                <span>No dose</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-green-500" />
                <span>Dose taken</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-yellow-200 ring-2 ring-yellow-400" />
                <span>Today</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}